import React, { useContext } from 'react';
import MenuItem from '@mui/material/MenuItem';
import Divider from '@mui/material/Divider';
import Link from '@mui/material/Link';
import List from '@mui/material/List';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';
import ArrowForwardIosSharpIcon from '@mui/icons-material/ArrowForwardIosSharp';
import MuiAccordion, { AccordionProps } from '@mui/material/Accordion';
import MuiAccordionSummary, {
  AccordionSummaryProps
} from '@mui/material/AccordionSummary';
import MuiAccordionDetails from '@mui/material/AccordionDetails';
import { AuthSession } from 'hooks/useAuth';
import {
  advisorAccountInformationMenuData,
  consumerAccountInformationMenuData,
  advisorMenuData,
  articlesMenuData,
  horoscopeMenuData
} from './menuData';
import { PrimaryHeaderContext } from '../PrimaryHeader/PrimaryHeader';

const Accordion = styled((props: AccordionProps) => (
  <MuiAccordion disableGutters elevation={0} square {...props} />
))(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  '&:not(:last-child)': {
    borderBottom: 0
  },
  '&:before': {
    display: 'none'
  }
}));

const AccordionSummary = styled((props: AccordionSummaryProps) => (
  <MuiAccordionSummary
    expandIcon={<ArrowForwardIosSharpIcon sx={{ fontSize: '0.9rem' }} />}
    {...props}
  />
))(({ theme }) => ({
  backgroundColor:
    theme.palette.mode === 'dark'
      ? 'rgba(255, 255, 255, .05)'
      : 'rgba(0, 0, 0, .03)',
  flexDirection: 'row-reverse',
  '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
    transform: 'rotate(90deg)'
  },
  '& .MuiAccordionSummary-content': {
    marginLeft: theme.spacing(1)
  }
}));

const AccordionDetails = styled(MuiAccordionDetails)(({ theme }) => ({
  padding: theme.spacing(2),
  borderTop: '1px solid rgba(0, 0, 0, .125)'
}));

function MenuLink({ link }) {
  const primaryHeaderContext = useContext(PrimaryHeaderContext);
  const { trackMixpanelEvent } = primaryHeaderContext;
  const onMenuOptionClick = ({ isTrackEvent, name }: NavigationItem) => {
    if (isTrackEvent && trackMixpanelEvent)
      trackMixpanelEvent('Main Category Clicked', { category: name }, true);
  };
  return (
    <MenuItem key={link.name}>
      <Link
        onClick={() => onMenuOptionClick(link)}
        key={link.name}
        href={link.href}
        underline="none"
        sx={{
          display: 'inline-block',
          width: '100%',
          color: 'black'
        }}>
        {link.name}
      </Link>
    </MenuItem>
  );
}

function AppDrawerMenu() {
  const [expanded, setExpanded] = React.useState<string | false>(false);
  const auth = AuthSession();
  const userIsAuthenticated = auth.isAuthenticated;
  const userIsAdvisor = auth.user && auth.user.isAdvisor;

  const handleChange =
    (panel: string) => (_event: React.SyntheticEvent, newExpanded: boolean) => {
      const expandedState = newExpanded ? panel : false;
      setExpanded(expandedState);
    };

  const renderMenu = (item: MainMenuItem) => {
    const { links } = item;

    return (
      <>
        {links.map(
          (link: NavigationItem) =>
            !link.isDesktopOnly && <MenuLink key={link.name} link={link} />
        )}{' '}
      </>
    );
  };

  return (
    <div>
      {!userIsAuthenticated && (
        <List>
          <MenuItem key="register">
            <Link
              key="register"
              href="/Registration/LoginStarter.aspx?Mode=StartWithReg&returnurl=/myaccountredirect&SourceUrl=/"
              underline="none"
              sx={{
                display: 'inline-block',
                width: '100%',
                color: 'black'
              }}>
              Register
            </Link>
          </MenuItem>
          <MenuItem>
            <Link
              key="login"
              href="/Registration/LoginStarter.aspx?Mode=StartWithLogin&returnurl=/myaccountredirect"
              underline="none"
              sx={{
                display: 'inline-block',
                width: '100%',
                color: 'black'
              }}>
              Sign In
            </Link>
          </MenuItem>
        </List>
      )}

      {userIsAuthenticated && (
        <List>
          <MenuItem sx={{ color: '#47686e' }}>
            Welcome {auth.user.username}
          </MenuItem>
          <MenuItem key="register">
            <Link
              key="register"
              href="/login/logout"
              underline="none"
              sx={{
                display: 'inline-block',
                width: '100%',
                color: 'black'
              }}>
              Sign Out
            </Link>
          </MenuItem>
        </List>
      )}

      <Divider />

      <div>
        {userIsAuthenticated && (
          <>
            <MenuItem key="myaccount">
              <Link
                key="myaccount"
                href={userIsAdvisor ? '/myaccount' : '/MyAccount/Consumer'}
                underline="none"
                sx={{
                  display: 'inline-block',
                  width: '100%',
                  color: 'black'
                }}>
                My Keen
              </Link>
            </MenuItem>
            <Accordion
              expanded={expanded === 'panel0'}
              onChange={handleChange('panel0')}>
              <AccordionSummary
                aria-controls="panel0d-content"
                id="panel0d-header">
                <Typography>Account Information</Typography>
              </AccordionSummary>
              <AccordionDetails>
                {renderMenu(
                  userIsAdvisor
                    ? advisorAccountInformationMenuData
                    : consumerAccountInformationMenuData
                )}
              </AccordionDetails>
            </Accordion>
          </>
        )}

        <Accordion
          expanded={expanded === 'panel1'}
          onChange={handleChange('panel1')}>
          <AccordionSummary aria-controls="panel1d-content" id="panel1d-header">
            <Typography>Advisors</Typography>
          </AccordionSummary>
          <AccordionDetails>{renderMenu(advisorMenuData)}</AccordionDetails>
        </Accordion>
        <Accordion
          expanded={expanded === 'panel2'}
          onChange={handleChange('panel2')}>
          <AccordionSummary aria-controls="panel2d-content" id="panel2d-header">
            <Typography>Articles</Typography>
          </AccordionSummary>
          <AccordionDetails>{renderMenu(articlesMenuData)}</AccordionDetails>
        </Accordion>
        <Accordion
          expanded={expanded === 'panel3'}
          onChange={handleChange('panel3')}>
          <AccordionSummary aria-controls="panel3d-content" id="panel3d-header">
            <Typography>Horoscopes</Typography>
          </AccordionSummary>
          <AccordionDetails>{renderMenu(horoscopeMenuData)}</AccordionDetails>
        </Accordion>
      </div>
      <List>
        <MenuItem key="readings101">
          <Link
            key="readings101"
            href="/psychic-advice"
            underline="none"
            sx={{
              display: 'inline-block',
              width: '100%',
              color: 'black'
            }}>
            Readings 101
          </Link>
        </MenuItem>
        <MenuItem key="help">
          <Link
            key="help"
            href={`${process.env.NEXT_PUBLIC_KEEN_HELP}/hc/en-us`}
            underline="none"
            sx={{
              display: 'inline-block',
              width: '100%',
              color: 'black'
            }}>
            Help
          </Link>
        </MenuItem>
      </List>
    </div>
  );
}

export default AppDrawerMenu;
